import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth,
  getMultiFactorResolver,
  getRedirectResult,
  GoogleAuthProvider,
  type MultiFactorError,
  type MultiFactorResolver,
  OAuthProvider,
  onIdTokenChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
  type UserCredential,
} from 'firebase/auth'
import { clearIndexedDbPersistence, doc, getDoc, getFirestore, setDoc, terminate } from 'firebase/firestore'
import { updateCurrentUserProfile, useCurrentUser } from 'vuefire'
import { resetLive } from '~/utils/liveData'

/**
 * Centralized authentication composable
 *
 * Provides consistent authentication operations with proper subscription management
 * and handles all Firebase auth-related functionality in one place.
 */
/** What the login page says after a sign-out (`?notice=` on /login) */
export type SignOutNotice = 'signed-out' | 'account-deleted'

export function useAuth() {
  const app = useFirebaseApp()
  const auth = getAuth(app)
  const user = useCurrentUser()
  const {
    registerSubscription,
    clearAllSubscriptions,
  } = useSubscriptions()
  const notificationsStore = useNotificationsStore()
  const membersStore = useMembersStore()
  const subkrewesStore = useSubKrewesStore()
  const tasksStore = useTasksStore()

  /**
   * Create a session for the authenticated user
   */
  const createSession = async (userCredential: { user: User }) => {
    try {
      // For initial login, always force refresh the token
      const idToken = await userCredential.user.getIdToken(true)

      const res = await $fetch<{ success: boolean, expiresAt?: number }>('/api/auth/create-session', {
        method: 'POST',
        body: {
          idToken,
          expiresIn: 60 * 60 * 24 * 14 * 1000, // 14 days in milliseconds
        },
      })

      if (res?.expiresAt) setStoredSessionExpiresAt(res.expiresAt)
    }
    catch (error) {
      console.error('Error creating session:', error)
      throw new Error('Failed to create session')
    }
  }

  /**
   * Create a member document in Firestore
   */
  const createMemberDoc = async (user: User, firstName: string, lastName: string) => {
    const db = getFirestore()
    const docRef = doc(db, 'members', user.uid)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      // Decode URL-encoded characters (Apple sign-in sometimes encodes spaces as +)
      const decodedFirstName = firstName.replace(/\+/g, ' ').trim()
      const decodedLastName = lastName.replace(/\+/g, ' ').trim()
      const fullName = `${decodedFirstName} ${decodedLastName}`.trim()

      await setDoc(docRef, {
        email: user.email,
        created_at: new Date(),
        updated_at: new Date(),
        member_type: 'pending',
        name: fullName,
        first_name: decodedFirstName,
        last_name: decodedLastName,
      }).catch((error) => {
        console.error('Error creating member document:', error)
        throw error
      })
    }
  }

  /**
   * Extract first and last name from SSO credential
   * Falls back to parsing displayName if profile data unavailable
   */
  const extractNamesFromCredential = (credential: UserCredential): { firstName: string, lastName: string } => {
    const additionalInfo = getAdditionalUserInfo(credential)
    const profile = additionalInfo?.profile as Record<string, unknown> | null

    // Google provides given_name and family_name
    // Apple provides firstName and lastName in the profile (only on first sign-in)
    const firstName = (profile?.given_name as string)
      || (profile?.firstName as string)
      || ''
    const lastName = (profile?.family_name as string)
      || (profile?.lastName as string)
      || ''

    // If we got names from profile, use them
    if (firstName || lastName) {
      return { firstName, lastName }
    }

    // Fallback: parse displayName
    const displayName = credential.user.displayName || ''
    const [parsedFirst, ...parsedRest] = displayName.trim().split(/\s+/)
    if (parsedFirst && parsedRest.length > 0) {
      return {
        firstName: parsedFirst,
        lastName: parsedRest.join(' '),
      }
    }
    return {
      firstName: displayName,
      lastName: '',
    }
  }

  /**
   * If an auth error indicates MFA is required, extract the resolver so the
   * caller can prompt the user for their second factor.
   */
  const extractMfaResolver = (error: unknown): MultiFactorResolver | null => {
    const code = (error as { code?: string })?.code
    if (code !== 'auth/multi-factor-auth-required') return null
    return getMultiFactorResolver(auth, error as MultiFactorError)
  }

  /**
   * Complete sign-in after a successful MFA challenge: create the server
   * session and ensure the Firestore member document exists.
   */
  const completeSignInAfterMfa = async (userCredential: UserCredential) => {
    try {
      await createSession(userCredential)
      const { firstName, lastName } = extractNamesFromCredential(userCredential)
      await createMemberDoc(userCredential.user, firstName, lastName)
      return { success: true as const, user: userCredential.user }
    }
    catch (error) {
      console.error('[Auth] Error completing sign-in after MFA:', error)
      return {
        success: false as const,
        error: error instanceof Error ? error : new Error('Unknown error completing sign-in'),
      }
    }
  }

  /**
   * Sign in with email and password
   */
  const signInWithEmail = async (email: string, password: string) => {
    try {
      console.warn('[Auth] Attempting to sign in with email')
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      console.warn('[Auth] Sign in successful, user:', userCredential.user.uid)

      // Create session and ensure member document exists
      await createSession(userCredential)
      const { firstName, lastName } = extractNamesFromCredential(userCredential)
      await createMemberDoc(userCredential.user, firstName, lastName)

      return { success: true, user: userCredential.user }
    }
    catch (error) {
      const mfaResolver = extractMfaResolver(error)
      if (mfaResolver) {
        return { success: false, mfaRequired: true, mfaResolver } as const
      }
      console.error('[Auth] Login error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during login'),
      }
    }
  }

  /**
   * Sign in with Google
   * Uses popup for local development (emulator doesn't support redirect)
   * Uses redirect for production (better mobile experience, avoids popup blockers)
   */
  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider()
      const runtimeConfig = useRuntimeConfig()
      const isEmulator = runtimeConfig.public.krewebacchusEnv === 'dev'

      if (isEmulator) {
        // Use popup for emulator - redirect doesn't work with emulator
        const result = await signInWithPopup(auth, provider)

        if (result?.user) {
          await createSession(result)
          const { firstName, lastName } = extractNamesFromCredential(result)
          await createMemberDoc(result.user, firstName, lastName)
          return { success: true, user: result.user }
        }
        return { success: false, error: new Error('No user returned from Google sign in') }
      }
      else {
        // Use redirect for production - better UX on mobile
        await signInWithRedirect(auth, provider)
        return { success: true, redirecting: true }
      }
    }
    catch (error) {
      const mfaResolver = extractMfaResolver(error)
      if (mfaResolver) {
        return { success: false, mfaRequired: true, mfaResolver } as const
      }
      console.error('Google login error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during Google login'),
      }
    }
  }

  /**
   * Handle the result from OAuth redirect sign-in (Google or Apple)
   * Call this on app initialization to complete the auth flow after redirect
   */
  const handleOAuthRedirectResult = async () => {
    try {
      const result = await getRedirectResult(auth)

      if (result?.user) {
        console.warn('[Auth] OAuth redirect sign in result')
        // Create session and ensure member document exists
        await createSession(result)
        const { firstName, lastName } = extractNamesFromCredential(result)
        await createMemberDoc(result.user, firstName, lastName)

        return { success: true, user: result.user }
      }
      // No result means user didn't come from a redirect
      return { success: false, noRedirect: true }
    }
    catch (error) {
      const mfaResolver = extractMfaResolver(error)
      if (mfaResolver) {
        return { success: false, mfaRequired: true, mfaResolver } as const
      }
      console.error('OAuth redirect result error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error handling OAuth redirect'),
      }
    }
  }

  // Keep old name for backwards compatibility
  const handleGoogleRedirectResult = handleOAuthRedirectResult

  /**
   * Sign in with Apple
   * Uses popup for local development (emulator doesn't support redirect)
   * Uses redirect for production (better mobile experience)
   */
  const signInWithApple = async () => {
    try {
      const provider = new OAuthProvider('apple.com')
      provider.addScope('email')
      provider.addScope('name')

      const runtimeConfig = useRuntimeConfig()
      const isEmulator = runtimeConfig.public.krewebacchusEnv === 'dev'

      if (isEmulator) {
        // Use popup for emulator - redirect doesn't work with emulator
        const result = await signInWithPopup(auth, provider)

        if (result?.user) {
          await createSession(result)
          const { firstName, lastName } = extractNamesFromCredential(result)
          await createMemberDoc(result.user, firstName, lastName)
          return { success: true, user: result.user }
        }
        return { success: false, error: new Error('No user returned from Apple sign in') }
      }
      else {
        // Use redirect for production - better UX on mobile
        await signInWithRedirect(auth, provider)
        return { success: true, redirecting: true }
      }
    }
    catch (error) {
      const mfaResolver = extractMfaResolver(error)
      if (mfaResolver) {
        return { success: false, mfaRequired: true, mfaResolver } as const
      }
      console.error('Apple login error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during Apple login'),
      }
    }
  }

  /**
   * Register a new user with email and password, and send the link that
   * confirms the address (the signup flow comes first, see the auth
   * middleware)
   */
  const registerWithEmail = async (email: string, password: string, firstName = '', lastName = '') => {
    try {
      const fullName = `${firstName} ${lastName}`.trim()
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      user.value = userCredential.user

      // The sign-in page doesn't ask for names any more: the signup flow
      // does, right after
      if (fullName) {
        await updateCurrentUserProfile({
          displayName: fullName,
        })
      }

      // Create session and member document
      await createSession(userCredential)
      await createMemberDoc(userCredential.user, firstName, lastName)
      sendEmailVerification(userCredential.user, {
        url: `${window.location.origin}/`,
      })
      return { success: true, user: userCredential.user }
    }
    catch (error) {
      console.error('Registration error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during registration'),
      }
    }
  }

  /**
   * Sign out the current user and clean up subscriptions. The page reloads
   * at the end, so a message for the user goes to the login page as `notice`.
   */
  const signOutUser = async (notice?: SignOutNotice) => {
    try {
      // Clean up store subscriptions
      notificationsStore.cleanupSubscriptions()

      // Clean up all tracked subscriptions, and stop every live source
      // (utils/liveData.ts), which empties what they supplied
      clearAllSubscriptions()
      resetLive()

      // Clear session from auth
      const { clearSession } = useAuthSession()

      await clearSession()

      await signOut(auth)

      // Reset user state
      user.value = null

      // Reset store states
      tasksStore.resetState()
      membersStore.$reset()
      subkrewesStore.$reset()
      notificationsStore.$reset()

      // Close the drawer: it can hold a member's record
      useDrawer().resetDrawerState()
    }
    catch (error) {
      console.error('Logout error:', error)
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error during logout'),
      }
    }

    // Check-in tablets and the like are shared: leave nothing of this member
    // behind. Starting the app over clears every store, useState and
    // Firestore's in-memory cache; the old IndexedDB cache goes too, if a tab
    // kept plugins/live-data.client.ts from deleting it
    await clearFirestoreCache()
    window.location.assign(notice ? `/login?notice=${notice}` : '/login')
    return { success: true }
  }

  const clearFirestoreCache = async () => {
    try {
      const db = getFirestore(app)
      await terminate(db)
      await clearIndexedDbPersistence(db)
    }
    catch (error) {
      // failed-precondition while another tab has the app open; sign out
      // anyway, that tab keeps the cache until it is closed
      console.warn('[Auth] Could not clear the offline cache:', error)
    }
  }

  /**
   * Subscribe to auth state changes
   * Uses onIdTokenChanged instead of onAuthStateChanged to ensure the
   * auth token is ready for Firestore before triggering subscriptions
   */
  const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
    const unsubscribe = onIdTokenChanged(auth, callback)
    registerSubscription(unsubscribe, 'auth')
    return unsubscribe
  }

  /**
   * Load current member data
   */
  const loadCurrentMember = async (): Promise<Member | null> => {
    if (!user.value?.uid) return null
    return await membersStore.fetchMember(user.value.uid)
  }

  /**
   * Check if the current user session is valid
   *
   * This now uses our token caching system to avoid unnecessary API calls
   */
  const checkSession = async () => {
    const { checkSession: checkSessionInternal, refreshSession } = useAuthSession()

    const isValid = await checkSessionInternal()
    if (!isValid) {
      // Try to refresh the session
      return await refreshSession()
    }

    return isValid
  }

  return {
    signInWithEmail,
    signInWithGoogle,
    signInWithApple,
    handleGoogleRedirectResult,
    registerWithEmail,
    signOutUser,
    checkSession,
    subscribeToAuthChanges,
    loadCurrentMember,
    completeSignInAfterMfa,
    // Expose the current user reactive ref
    user,
    createSession, // Expose createSession for other uses if needed
  }
}
